import {
  Sparkles,
  Rocket,
  Bug,
  Wrench,
  Shield,
  Zap,
  type LucideIcon,
} from 'lucide-react'

// ── Workflow Status Constants ─────────────────────────────────────────────────

export const WORKFLOW_STATUS = {
  DRAFT: 'draft',
  PENDING: 'pending',
  NEEDS_INPUT: 'needs_input',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  FAILED: 'failed',
  REJECTED: 'rejected',
  ARCHIVED: 'archived',
} as const

export type WorkflowStatus = typeof WORKFLOW_STATUS[keyof typeof WORKFLOW_STATUS]

// ── Orch Status Constants (UI-layer aggregation) ─────────────────────────────

export const ORCH_STATUS = {
  DRAFT: 'DRAFT',
  PENDING: 'PENDING',
  NEEDS_INPUT: 'NEEDS_INPUT',
  EXECUTING: 'EXECUTING',
  DONE: 'DONE',
  THINKING: 'THINKING',
} as const

export type OrchStatus = typeof ORCH_STATUS[keyof typeof ORCH_STATUS]

// ── Status → OrchStatus Mapping ───────────────────────────────────────────────

export const statusToOrchMap: Record<WorkflowStatus, OrchStatus> = {
  [WORKFLOW_STATUS.DRAFT]: ORCH_STATUS.DRAFT,
  [WORKFLOW_STATUS.PENDING]: ORCH_STATUS.PENDING,
  [WORKFLOW_STATUS.NEEDS_INPUT]: ORCH_STATUS.NEEDS_INPUT,
  [WORKFLOW_STATUS.IN_PROGRESS]: ORCH_STATUS.EXECUTING,
  [WORKFLOW_STATUS.COMPLETED]: ORCH_STATUS.DONE,
  [WORKFLOW_STATUS.FAILED]: ORCH_STATUS.DONE,
  [WORKFLOW_STATUS.REJECTED]: ORCH_STATUS.DONE,
  [WORKFLOW_STATUS.ARCHIVED]: ORCH_STATUS.DONE,
}

// ── Status Colors ─────────────────────────────────────────────────────────────

export const statusColors: Record<string, { color: string; bg: string; label: string }> = {
  [WORKFLOW_STATUS.DRAFT]: { color: 'text-zinc-400', bg: 'bg-zinc-400/10', label: 'Draft' },
  [WORKFLOW_STATUS.PENDING]: { color: 'text-amber-400', bg: 'bg-amber-400/10', label: 'Pending Review' },
  [WORKFLOW_STATUS.NEEDS_INPUT]: { color: 'text-amber-300', bg: 'bg-amber-300/10', label: 'Needs Input' },
  [WORKFLOW_STATUS.IN_PROGRESS]: { color: 'text-blue-400', bg: 'bg-blue-400/10', label: 'Running' },
  [WORKFLOW_STATUS.COMPLETED]: { color: 'text-emerald-400', bg: 'bg-emerald-400/10', label: 'Completed' },
  [WORKFLOW_STATUS.FAILED]: { color: 'text-red-400', bg: 'bg-red-400/10', label: 'Failed' },
  [WORKFLOW_STATUS.REJECTED]: { color: 'text-slate-400', bg: 'bg-slate-400/10', label: 'Rejected' },
  [WORKFLOW_STATUS.ARCHIVED]: { color: 'text-zinc-600', bg: 'bg-zinc-600/10', label: 'Archived' },
}

// ── Priority Border Colors (for WorkflowCard left border) ────────────────────

export const priorityBorderColors: Record<string, string> = {
  low: 'border-l-blue-500',
  medium: 'border-l-amber-500',
  high: 'border-l-orange-500',
  critical: 'border-l-red-500',
}

// ── Type Configuration ────────────────────────────────────────────────────────

export const typeConfig: Record<string, { icon: LucideIcon; color: string; bg: string; label: string }> = {
  improvement: { icon: Sparkles, color: 'text-purple-400', bg: 'bg-purple-400/10', label: 'Improvement' },
  feature: { icon: Rocket, color: 'text-blue-400', bg: 'bg-blue-400/10', label: 'Feature' },
  bugfix: { icon: Bug, color: 'text-red-400', bg: 'bg-red-400/10', label: 'Bug Fix' },
  refactor: { icon: Wrench, color: 'text-amber-400', bg: 'bg-amber-400/10', label: 'Refactor' },
  security: { icon: Shield, color: 'text-rose-400', bg: 'bg-rose-400/10', label: 'Security' },
  performance: { icon: Zap, color: 'text-emerald-400', bg: 'bg-emerald-400/10', label: 'Performance' },
}

// ── Priority Configuration ────────────────────────────────────────────────────

export const priorityConfig: Record<string, { color: string; bg: string; label?: string }> = {
  low: { color: 'text-slate-400', bg: 'bg-slate-400/10', label: 'Low' },
  medium: { color: 'text-amber-400', bg: 'bg-amber-400/10', label: 'Medium' },
  high: { color: 'text-orange-400', bg: 'bg-orange-400/10', label: 'High' },
  critical: { color: 'text-red-400', bg: 'bg-red-400/10', label: 'Critical' },
}

export const priorityOrder: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 }

// ── Triage Status Colors (text/bg variants for TodoTriage badges) ─────────────

export const triageStatusColors: Record<string, { text: string; bg: string }> = {
  [WORKFLOW_STATUS.PENDING]: { text: 'text-amber-400', bg: 'bg-amber-400/10' },
  [WORKFLOW_STATUS.NEEDS_INPUT]: { text: 'text-amber-300', bg: 'bg-amber-300/10' },
}

// ── Status Sort Priority ──────────────────────────────────────────────────────

export const statusSortPriority: Record<OrchStatus, number> = {
  [ORCH_STATUS.NEEDS_INPUT]: 0,
  [ORCH_STATUS.EXECUTING]: 1,
  [ORCH_STATUS.PENDING]: 2,
  [ORCH_STATUS.DRAFT]: 3,
  [ORCH_STATUS.DONE]: 4,
  [ORCH_STATUS.THINKING]: 5,
}

// ── Legacy statusConfig (deprecated, use statusColors) ────────────────────────

export const statusConfig: Record<string, { color: string; bg: string; label: string }> = {
  completed: { color: 'text-emerald-400', bg: 'bg-emerald-400/10', label: 'Completed' },
  failed: { color: 'text-red-400', bg: 'bg-red-400/10', label: 'Failed' },
  in_progress: { color: 'text-blue-400', bg: 'bg-blue-400/10', label: 'Running' },
  rejected: { color: 'text-slate-400', bg: 'bg-slate-400/10', label: 'Rejected' },
  pending: { color: 'text-amber-400', bg: 'bg-amber-400/10', label: 'Pending' },
}
